import React from "react";
import { Plus, Minus, ShoppingCart, Calendar, AlertTriangle, Clock, CheckCircle2, Layers } from "lucide-react";
import {
  getAddonRateDisplay,
  checkAddonDateCompatibility,
  getAddonScheduleOpenings,
  getAddonDefaultUnits,
} from "../../utils/addonPricingUtils";

const AddonSelector = ({
  addons,
  selectedAddons,
  onChange,
  currency = "KES",
  selectedDate = null,
  bookingDetails = null,
  spaceAvailableSlots = null,
  onSelectDate = null,
  spaceDurationType = "HOURLY",
}) => {
  if (!addons || addons.length === 0) return null;

  const handleUpdateQuantity = (addonId, delta) => {
    const addon = addons.find(a => a.id === addonId);
    const maxQuantity =
      typeof addon?.numberOfInstances === "number" && addon.numberOfInstances > 0
        ? addon.numberOfInstances
        : Infinity;

    const currentAddon = selectedAddons.find(a => a.addonId === addonId || a.id === addonId);
    const currentQuantity = currentAddon ? currentAddon.quantity : 0;
    const targetQuantity = currentQuantity + delta;
    const newQuantity = Math.min(maxQuantity, Math.max(0, targetQuantity));

    let newSelectedAddons;
    if (newQuantity === 0) {
      newSelectedAddons = selectedAddons.filter(a => a.addonId !== addonId && a.id !== addonId);
    } else if (currentAddon) {
      newSelectedAddons = selectedAddons.map(a =>
        a.addonId === addonId || a.id === addonId
          ? {
              ...a,
              addonId,
              id: addonId,
              quantity: newQuantity,
              name: addon?.name || a.name,
              price: addon?.unitPrice ?? addon?.price ?? a.price ?? 0,
              unitPrice: addon?.unitPrice ?? addon?.price ?? a.unitPrice ?? 0,
              duration: addon?.duration || a.duration || "FIXED",
              numberOfInstances: addon?.numberOfInstances ?? a.numberOfInstances,
              availableSlots: addon?.availableSlots || addon?.availabilitySlots || a.availableSlots,
            }
          : a
      );
    } else {
      const { defaultUnits } = getAddonDefaultUnits(addon, bookingDetails, spaceDurationType);
      newSelectedAddons = [
        ...selectedAddons,
        {
          addonId,
          id: addonId,
          quantity: newQuantity,
          name: addon?.name || "Add-on",
          price: addon?.unitPrice ?? addon?.price ?? 0,
          unitPrice: addon?.unitPrice ?? addon?.price ?? 0,
          duration: addon?.duration || "FIXED",
          numberOfInstances: addon?.numberOfInstances,
          availableSlots: addon?.availableSlots || addon?.availabilitySlots,
          customDurationUnits: defaultUnits,
        },
      ];
    }

    onChange(newSelectedAddons);
  };

  const handleUpdateDurationUnits = (addonId, newDurationUnits) => {
    const newSelectedAddons = selectedAddons.map(a => 
      a.addonId === addonId || a.id === addonId 
        ? { ...a, customDurationUnits: newDurationUnits } 
        : a
    );
    onChange(newSelectedAddons);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-slate-900">Available Add-ons</h3>
        </div>
        <span className="text-[11px] text-slate-500 font-medium">
          Choose equipment or services for your booking
        </span>
      </div>

      <div className="space-y-3">
        {addons.map(addon => {
          const selected = selectedAddons.find(a => a.addonId === addon.id || a.id === addon.id);
          const quantity = selected ? selected.quantity : 0;
          const maxQuantity =
            typeof addon.numberOfInstances === "number" && addon.numberOfInstances > 0
              ? addon.numberOfInstances
              : Infinity;
          const isMaxReached = quantity >= maxQuantity;

          const { defaultUnits, maxUnits, minUnits, unitLabel, isAdjustable } = getAddonDefaultUnits(
            addon,
            bookingDetails,
            spaceDurationType
          );
          const currentDurationUnits = selected?.customDurationUnits ?? defaultUnits;

          const compatibility = checkAddonDateCompatibility(
            addon,
            selectedDate,
            bookingDetails,
            spaceAvailableSlots
          );

          const {
            isCompatible,
            hasConstraint,
            availableDaysSummary,
            scheduleOpenings,
            selectedDayLabel,
            nextAvailableDates,
            warningMessage,
          } = compatibility;

          const hasTargetTime = Boolean(selectedDate || bookingDetails?.startTime);
          const showScheduleWarning = hasConstraint && !isCompatible && (quantity > 0 || hasTargetTime);

          return (
            <div
              key={addon.id}
              className={`p-3.5 rounded-xl border transition-all ${
                quantity > 0
                  ? isCompatible
                    ? "bg-blue-50/50 border-blue-200 shadow-xs"
                    : "bg-amber-50/60 border-amber-300 shadow-xs"
                  : "bg-white border-slate-200/90 hover:border-slate-300"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Title and tags */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-1">
                    <span className="text-sm font-semibold text-slate-900">{addon.name}</span>

                    {addon.duration && addon.duration !== "FIXED" && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {addon.duration}
                      </span>
                    )}

                    {/* Capacity / Available Instances Badge */}
                    {typeof addon.numberOfInstances === "number" && addon.numberOfInstances > 0 && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200/80 flex items-center gap-1">
                        <Layers className="w-2.5 h-2.5 text-slate-500" />
                        {addon.numberOfInstances} unit{addon.numberOfInstances > 1 ? "s" : ""} max
                      </span>
                    )}
                  </div>

                  {/* Rate / Pricing */}
                  <div className="text-xs text-slate-700 font-semibold mb-1.5">
                    {getAddonRateDisplay(addon, currency)}
                  </div>

                  {addon.description && (
                    <p className="text-[11px] text-slate-500 line-clamp-1 mb-2">{addon.description}</p>
                  )}

                  {/* Available Openings / Schedule Openings Pill */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    {scheduleOpenings?.hasSchedule ? (
                      scheduleOpenings.openings.map((opening, oIdx) => (
                        <span
                          key={oIdx}
                          className="text-[10.5px] font-medium px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200/70 inline-flex items-center gap-1.5"
                          title={`Available: ${opening.formatted}`}
                        >
                          <Clock className="w-3 h-3 text-slate-500 shrink-0" />
                          <span>{opening.formatted}</span>
                        </span>
                      ))
                    ) : (
                      <span className="text-[10.5px] font-medium px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3 text-emerald-600 shrink-0" />
                        <span>Available with any space slot</span>
                      </span>
                    )}

                    {/* Active Match Indicator when selected */}
                    {quantity > 0 && hasConstraint && hasTargetTime && isCompatible && (
                      <span className="text-[10.5px] font-medium px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                        <span>Matches selected slot</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Right Side Controls */}
                <div className="flex flex-col items-end gap-3 shrink-0 pt-0.5">
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleUpdateQuantity(addon.id, -1)}
                      disabled={quantity === 0}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 transition-colors ${
                        quantity === 0
                          ? "opacity-30 cursor-not-allowed bg-slate-50"
                          : "hover:bg-white hover:border-slate-300 bg-slate-50 active:bg-slate-100 text-slate-700"
                      }`}
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-3.5 h-3.5 text-slate-700" />
                    </button>

                    <span className="w-6 text-center text-sm font-semibold text-slate-900">{quantity}</span>

                    <button
                      type="button"
                      onClick={() => handleUpdateQuantity(addon.id, 1)}
                      disabled={isMaxReached}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                        isMaxReached
                          ? "opacity-30 cursor-not-allowed bg-slate-100 border-slate-200"
                          : "border-slate-200 bg-white hover:border-slate-300 active:bg-slate-50 text-slate-700"
                      }`}
                      title={isMaxReached ? `Maximum quantity of ${maxQuantity} reached` : "Add quantity"}
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-3.5 h-3.5 text-slate-700" />
                    </button>
                  </div>
                  
                  {/* Duration Controls */}
                  {quantity > 0 && isAdjustable && (
                    <div className="flex flex-col items-end gap-1 mt-1">
                      <div className="text-[10px] text-slate-500 font-medium">
                        {unitLabel.replace(/s$/, "").charAt(0).toUpperCase() + unitLabel.replace(/s$/, "").slice(1)}s used:
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleUpdateDurationUnits(addon.id, currentDurationUnits - 1)}
                          disabled={currentDurationUnits <= minUnits}
                          className={`w-6 h-6 flex items-center justify-center rounded border transition-colors ${
                            currentDurationUnits <= minUnits
                              ? "opacity-30 cursor-not-allowed bg-slate-50 border-slate-200"
                              : "border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 text-slate-700 active:bg-slate-100"
                          }`}
                          aria-label="Decrease duration"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-5 text-center text-xs font-semibold text-slate-900">{currentDurationUnits}</span>
                        <button
                          type="button"
                          onClick={() => handleUpdateDurationUnits(addon.id, currentDurationUnits + 1)}
                          disabled={currentDurationUnits >= maxUnits}
                          className={`w-6 h-6 flex items-center justify-center rounded border transition-colors ${
                            currentDurationUnits >= maxUnits
                              ? "opacity-30 cursor-not-allowed bg-slate-50 border-slate-200"
                              : "border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 text-slate-700 active:bg-slate-100"
                          }`}
                          aria-label="Increase duration"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-[9px] text-slate-400">
                        / {maxUnits} {unitLabel}{maxUnits > 1 && !unitLabel.endsWith("s") ? "s" : ""} max
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Schedule constraint notification and clickable date switch helper */}
              {showScheduleWarning && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 space-y-2 animate-in fade-in duration-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 leading-relaxed">
                      <span className="font-semibold text-amber-950">Schedule Notice:</span>{" "}
                      {warningMessage || (
                        <>
                          {addon.name} is only available on{" "}
                          <span className="font-semibold text-amber-950">{availableDaysSummary}</span>.
                          {selectedDayLabel && (
                            <span> Your selected booking date is <span className="font-medium text-amber-950">{selectedDayLabel}</span>.</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {nextAvailableDates.length > 0 && (
                    <div className="pt-1.5 border-t border-amber-200/80 flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] font-medium text-amber-800 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-amber-600" />
                        Pick a matching date:
                      </span>
                      {nextAvailableDates.map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => onSelectDate && onSelectDate(item.date)}
                          className="px-2.5 py-1 bg-white hover:bg-amber-100 border border-amber-300 rounded-md text-[11px] font-semibold text-amber-900 transition-all shadow-2xs hover:shadow-xs inline-flex items-center gap-1 cursor-pointer active:scale-95"
                          title={`Switch booking date to ${item.dayLabel}, ${item.formatted}`}
                        >
                          <span className="text-amber-700">📅</span>
                          <span>{item.formatted}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AddonSelector;
